import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Badge } from "react-bootstrap";
import api from "../axiosConfig";
import { AuthContext } from "./AuthContext";

export const MaintenanceContext = createContext();

export const MaintenanceProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);

  /* STATE */
  const [machines, setMachines] = useState([]);
  const [needMaintenanceMachines, setNeedMaintenanceMachines] = useState([]);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [latestActivities, setLatestActivities] = useState([]);
  const [shortLifetimeItems, setShortLifetimeItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());

  const machinesIntervalRef = useRef(null);
  const activitiesIntervalRef = useRef(null);

  /* FETCH MACHINES */
  const fetchMachines = useCallback(
    async (silent = false) => {
      if (!currentUser) return;
      try {
        if (!silent) setLoading(true);
        setError("");

        const res = await api.get("/machines");
        const normalized = res.data.map((m) => ({
          ...m,
          id: m.id ?? m.machine_id ?? m._id,
        }));

        const needFix = normalized.filter(
          (m) =>
            m.status === "Need Maintenance" || m.status === "Under Maintenance",
        );

        setMachines(normalized);
        setNeedMaintenanceMachines(needFix);
        setLastUpdateTime(new Date());
      } catch (err) {
        console.error(err);
        setError("Gagal mengambil data mesin");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [currentUser],
  );

  /* FETCH HISTORY */
  const fetchMaintenanceHistory = useCallback(async () => {
    try {
      const res = await api.get("/maintenance/history-with-usage");
      setMaintenanceHistory(res.data);
    } catch (err) {
      console.error("History error:", err);
    }
  }, []);

  /* FETCH LATEST ACTIVITIES */
  const fetchLatestActivities = useCallback(async (limit = 5) => {
    try {
      const res = await api.get(`/maintenance/history?limit=${limit}`);
      const normalized = res.data.map((row) => ({
        id: row.id,
        issue: row.issue,
        solution: row.solution,
        startTime: row.start_time,
        endTime: row.end_time,
        machine: {
          id: row.machine_id,
          code: row.machine_code,
          name: row.machine_name,
          status: row.machine_status,
        },
        sector: {
          id: row.sector_id,
          name: row.sector_name,
        },
      }));
      setLatestActivities(normalized);
    } catch (err) {
      console.error("Latest activity error:", err);
    }
  }, []);

  /* FORCE REFRESH */
  const forceRefresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchMachines(true), fetchLatestActivities(5)]);
    setLoading(false);
  }, [fetchMachines, fetchLatestActivities]);

  /* AUTO POLLING */
  useEffect(() => {
    if (!currentUser) return;

    fetchMachines();
    fetchLatestActivities(5);

    machinesIntervalRef.current = setInterval(() => fetchMachines(true), 10000);
    activitiesIntervalRef.current = setInterval(
      () => fetchLatestActivities(5),
      15000,
    );

    return () => {
      clearInterval(machinesIntervalRef.current);
      clearInterval(activitiesIntervalRef.current);
    };
  }, [currentUser, fetchMachines, fetchLatestActivities]);

  /* START MAINTENANCE */
  const handleMarkUnderMaintenance = async (machineId) => {
    if (!machineId) return false;

    try {
      await api.post(`/maintenance/start/${machineId}`);
      await fetchMachines(true);
      setMachines((prev) =>
        prev.map((m) =>
          m.id === machineId ? { ...m, status: "Under Maintenance" } : m,
        ),
      );

      return true;
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.msg || "Gagal memulai maintenance");
      return false;
    }
  };

  /* FINISH MAINTENANCE - FIXED VERSION */
  const finishMaintenance = async (
    machineId,
    solutionData,
    spareparts,
  ) => {
    if (!machineId || !solutionData) return false;

    // spareparts bisa dikirim sebagai argumen ke-3, ATAU diselipkan
    // di dalam solutionData (payload) oleh form finish maintenance.
    const rawSpareparts =
      spareparts !== undefined && spareparts !== null
        ? spareparts
        : solutionData.spareparts;

    // pastikan spareparts valid
    const sparepartsPayload = Array.isArray(rawSpareparts)
      ? rawSpareparts.filter((sp) => sp.itemId && sp.qty > 0)
      : [];

    // Kirim payload langsung ke root, jangan di dalam object solution
    const payload = {
      uraianMasalah: solutionData.uraianMasalah || "",
      gejalaMasalah: solutionData.gejalaMasalah || "",
      tindakanPenyebab: solutionData.tindakanPenyebab || "",
      evakuasiPerbaikan: solutionData.evakuasiPerbaikan || "",
      dikerjakanOleh: solutionData.dikerjakanOleh || "",
      dicekOleh: solutionData.dicekOleh || "",
      keterangan: solutionData.keterangan || "",
      spareparts: sparepartsPayload,
    };

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await api.post(`/maintenance/finish/${machineId}`, payload);
      await fetchMachines(true);
      setMachines((prev) =>
        prev.map((m) =>
          m.id === machineId ? { ...m, status: "Operational" } : m,
        ),
      );

      setSuccess("Maintenance selesai");
      return true;
    } catch (err) {
      console.error("Finish maintenance error:", err);
      setError(err.response?.data?.msg || "Gagal menyelesaikan maintenance");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleMarkFixed = async (machineId, payload) => {
    return await finishMaintenance(machineId, payload, shortLifetimeItems);
  };

  /* SHORT LIFETIME STOCK */
  const addShortLifetimeItem = (item) =>
    setShortLifetimeItems((prev) => [...prev, { ...item, id: Date.now() }]);
  const removeShortLifetimeItem = (id) =>
    setShortLifetimeItems((prev) => prev.filter((i) => i.id !== id));
  const updateShortLifetimeItem = (id, data) =>
    setShortLifetimeItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, ...data } : i)),
    );

  /* HELPERS */
  const getDowntime = (startTime) => {
    if (!startTime) return "-";
    const diff = Math.floor((Date.now() - new Date(startTime)) / 60000);
    return diff < 60 ? `${diff} menit` : `${Math.floor(diff / 60)} jam`;
  };

  const getStatusBadge = (status) => {
    const map = {
      Operational: ["Beroperasi", "success"],
      "Need Maintenance": ["Butuh Perbaikan", "danger"],
      "Under Maintenance": ["Sedang Diperbaiki", "warning"],
    };
    const [text, bg] = map[status] || [status, "secondary"];
    return <Badge bg={bg}>{text}</Badge>;
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString("id-ID") : "-");

  /* PROVIDER */
  return (
    <MaintenanceContext.Provider
      value={{
        // data
        machines,
        needMaintenanceMachines,
        maintenanceHistory,
        recentActivities: latestActivities,
        shortLifetimeItems,

        // ui
        loading,
        error,
        success,
        lastUpdateTime,

        // actions
        fetchMachines,
        fetchMaintenanceHistory,
        fetchLatestActivities,
        forceRefresh,
        handleMarkUnderMaintenance,
        handleMarkFixed,
        finishMaintenance,

        // stock
        addShortLifetimeItem,
        removeShortLifetimeItem,
        updateShortLifetimeItem,

        // helpers
        getDowntime,
        getStatusBadge,
        formatDate,
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
};

export default MaintenanceContext;
